import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, Input, ViewEncapsulation, forwardRef, Inject, Optional, Output, EventEmitter } from '@angular/core';
import { FocusableOption, FocusMonitor } from '@angular/cdk/a11y';

import { PhxDividerComponent } from '../../divider/phx-divider.component';
import { PhxMenuComponent } from '../phx-menu/phx-menu.component';
import { ChevronRight } from '../../icon/icon-map';
import { PhxDropdownService } from '../../dropdown/phx-dropdown.service';

@Component({
  selector: 'phx-menu-item, [phx-menu-item]',
  templateUrl: './phx-menu-item.component.html',
  styleUrls: ['./phx-menu-item.component.scss'],
  inputs: ['disabled'],
  host: {
    role: 'menuitem',
    '[attr.tabindex]': 'isDisabled ? null : -2',
    class: 'phx-menu-item',
    '[class.phx-menu-item-disabled]': 'isDisabled',
    '[class.phx-menu-item-divider]': 'hasDivider',
    '[class.phx-menu-item-has-nested-menu]': 'hasNestedMenu',
    '(click)': 'onClick()',
    '(keydown.enter)': 'onClick()',
    '(keydown.space)': 'onSpaceKeyPressed($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxMenuItemComponent implements FocusableOption {
  @Input() phxCondensed: boolean = false;
  @Input() disabled: boolean = false;

  @Output() phxClick = new EventEmitter<void>();
  @ContentChild(PhxDividerComponent) divider: PhxDividerComponent;
  @ContentChild(forwardRef(() => PhxMenuComponent)) nestedMenu?: PhxMenuComponent;

  public ChevronRight : string = ChevronRight;

  public get hasDivider(): boolean {
    return !!this.divider;
  }

  public get hasNestedMenu(): boolean {
    return !!this.nestedMenu;
  }

  public get isDisabled(): boolean {
    return this.hasDivider || this.disabled;
  }

  constructor(
    private host: ElementRef<HTMLElement>,
    @Inject(forwardRef(() => PhxMenuComponent)) public menuComponent: PhxMenuComponent,
    public elementRef: ElementRef,
    protected focusMonitor: FocusMonitor,
    @Optional() private phxDropdownService: PhxDropdownService,
  ) {
  }

  public focus(): void {
    this.host.nativeElement?.focus();
  }

  public getLabel(): string {
    return this.host.nativeElement?.querySelector('.phx-menu-item-content')?.textContent?.trim() ?? '';
  }

  public onClick(): void {
    if (this.isDisabled) {
      return;
    }

    this.phxClick.emit();

    if (this.hasNestedMenu) {
      const label = this.host.nativeElement?.querySelector('.phx-menu-item-content')?.textContent?.trim();
      this.menuComponent.setSubmenu(this.nestedMenu, label);
      return;
    }

    if (this.phxDropdownService) {
      this.phxDropdownService.close();
    }
  }

  public onSpaceKeyPressed(event: KeyboardEvent): void {
    event.preventDefault();
    this.onClick();
  }
}

@Component({
  selector: 'phx-menu-item-link',
  templateUrl: './phx-menu-item.component.html',
  styleUrls: ['./phx-menu-item.component.scss'],
  host: {
    role: 'menuitem',
    '[attr.tabindex]': 'isDisabled ? null : -2',
    class: 'phx-menu-item phx-menu-item-link',
    '[class.phx-menu-item-disabled]': 'isDisabled',
    '(click)': 'onClick()',
    '(keydown.enter)': 'onClick()',
    '(keydown.space)': 'onSpaceKeyPressed($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxMenuItemLinkComponent extends PhxMenuItemComponent {
  constructor(
    host: ElementRef<HTMLElement>,
    @Inject(forwardRef(() => PhxMenuComponent)) menuComponent: PhxMenuComponent,
    elementRef: ElementRef,
    focusMonitor: FocusMonitor,
    @Optional() phxDropdownService: PhxDropdownService,
  ) {
    super(host, menuComponent, elementRef, focusMonitor, phxDropdownService);
  }
}
